import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'

// Standalone navigate — usable outside React components
export const navigate = (path: string) => {
  window.history.pushState({}, '', path)
  window.dispatchEvent(new Event('pushstate-changed'))
}

interface RouterContextValue {
  currentPath: string
  navigate: (path: string) => void
}

const RouterContext = createContext<RouterContextValue | undefined>(undefined)

export const RouterProvider = ({ children }: { children: ReactNode }) => {
  const [currentPath, setCurrentPath] = useState(window.location.pathname)

  useEffect(() => {
    const sync = () => setCurrentPath(window.location.pathname)
    window.addEventListener('popstate', sync)
    window.addEventListener('pushstate-changed', sync)
    return () => {
      window.removeEventListener('popstate', sync)
      window.removeEventListener('pushstate-changed', sync)
    }
  }, [])

  return (
    <RouterContext.Provider value={{ currentPath, navigate }}>{children}</RouterContext.Provider>
  )
}

export const useRouter = () => {
  const ctx = useContext(RouterContext)
  if (!ctx) {
    throw new Error('useRouter must be used within a RouterProvider')
  }
  return ctx
}

export const useRoute = <P extends Record<string, string>>(pattern: string): P | null => {
  const { currentPath } = useRouter()
  const patternParts = pattern.split('/').filter(Boolean)
  const pathParts = currentPath.split('/').filter(Boolean)

  if (patternParts.length !== pathParts.length) {
    return null
  }

  const params: Record<string, string> = {}
  for (let i = 0; i < patternParts.length; i++) {
    const seg = patternParts[i]
    if (seg.startsWith(':')) {
      params[seg.slice(1)] = pathParts[i]
    } else if (seg !== pathParts[i]) {
      return null
    }
  }
  return params as P
}
